import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Stack, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CreateApi from './Settings/appSettings';
import { deleteCookie } from './contexts/CookieContext';
import { useAuth } from "react-oidc-context";
import { useMCContext } from './contexts/useMCStates';


export const RegistrationSuccess = () => {
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [amountPaid, setAmountPaid] = useState<number | null>(null);
    const location = useLocation();
    const auth = useAuth();
    const { setCartItems } = useMCContext();
    const api = CreateApi();
    const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params?.get('redirect');
    const sessionId = new URLSearchParams(location.search).get('session_id');

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            return;
        }

        let attempts = 0;
        const maxAttempts = 20; // poll for up to ~40 seconds

        const poll = async () => {
            try {
                const res = await api.get(`/mc/checkout-status?sessionId=${sessionId}`);
                const data = res.data;

                if (data.status === 'completed') {
                    if (data.amountTotal != null) setAmountPaid(data.amountTotal);
                    if (auth.isAuthenticated && auth.user?.profile?.sub) {
                        // Auth is already loaded — delete the cookie now and no flag needed.
                        deleteCookie(`${auth.user.profile.sub}-cartItems`);
                    } else {
                        // Auth hasn't loaded yet — set a flag so MomentCentral's effect
                        // deletes the cookie once auth is available (race condition guard).
                        sessionStorage.setItem('cart-cleared', '1');
                    }
                    setCartItems([]);
                    setStatus('success');
                    return;
                }

                attempts++;
                if (attempts < maxAttempts) {
                    pollingRef.current = setTimeout(poll, 1000);
                } else {
                    setStatus('error');
                }
            } catch (err) {
                console.error('Error polling checkout status:', err);
                attempts++;
                if (attempts < maxAttempts) {
                    pollingRef.current = setTimeout(poll, 1000);
                } else {
                    setStatus('error');
                }
            }
        };

        poll();

        return () => {
            if (pollingRef.current) clearTimeout(pollingRef.current);
        };
    }, [sessionId]);

    if (status === 'loading') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                    <Typography variant="body1" color="text.secondary">
                        Processing your registration, please wait…
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (status === 'error') {
        return (
            <Box sx={{ p: 4, maxWidth: 800, margin: '0 auto' }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <ErrorOutlineIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Payment Received — Registration Pending
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Your payment was processed successfully, but your registration is taking
                        longer than expected to confirm. This can happen due to high traffic.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please contact support with your session ID:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1, wordBreak: 'break-all' }}>
                        {sessionId ?? 'N/A'}
                    </Typography>
                    {redirectUrl && (
                        <Button
                            sx={{ mt: 3 }}
                            onClick={() => { window.location.href = decodeURIComponent(redirectUrl); }}
                        >
                            Go to Events
                        </Button>
                    )}
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, maxWidth: 800, margin: '0 auto' }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                    Registration Successful!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Thank you for your registration. You will receive a confirmation email within 24 hours.
                </Typography>
                {amountPaid != null && (
                    <Box sx={{ mt: 4, textAlign: 'left' }}>
                        <Typography variant="h6" gutterBottom>
                            Order Details:
                        </Typography>
                        <Typography>
                            Amount Paid: ${(amountPaid / 100).toFixed(2)}
                        </Typography>
                    </Box>
                )}
                <Stack direction='column' spacing={2} sx={{ mt: 4 }}>
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="h6" gutterBottom>
                            You can now close this page or go back to the events page.
                        </Typography>
                    </Box>
                    {redirectUrl && (
                        <Button onClick={() => { window.location.href = decodeURIComponent(redirectUrl); }}>
                            Go to Events
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};
